import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { 
  MessageCircle, Heart, Repeat2, Send, Search, TrendingUp, Users, 
  CheckCircle, Plus, X, Edit2, Trash2, MoreVertical, ExternalLink,
  Mail, UserPlus as UserPlusIcon, MessageSquare, Settings, DollarSign,
  Sparkles, Languages
} from 'lucide-react';
import { MatriseConnectPage } from '../user/MatriseConnectPage';
import { AdCard } from '../../components/ads/AdCard';
import { NotificationBell } from '../../components/connect/NotificationBell';
import { PostPreviewModal } from '../../components/connect/PostPreviewModal';
import { TipButton } from '../../components/connect/TipButton';
import { RunnersTab } from '../../components/connect/RunnersTab';
import { BecomeRunnerButton } from '../../components/connect/BecomeRunnerButton';
import { formatTimeAgo } from '../../utils/helpers';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function UnifiedConnectPage() {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const initialTab = queryParams.get('tab') || 'for-you';
  const { user } = useAuth();
  
  const [activeTab, setActiveTab] = useState(initialTab);
  const [posts, setPosts] = useState([]);
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [newPost, setNewPost] = useState('');
  const [postData, setPostData] = useState({ content: '', media_url: null, media_type: null });
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [adFrequency, setAdFrequency] = useState(10);
  const [dmPrivacy, setDmPrivacy] = useState('public');
  const [showPrivacySettings, setShowPrivacySettings] = useState(false);
  const [showAIAssistant, setShowAIAssistant] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState('');
  const [showTipDialog, setShowTipDialog] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (activeTab !== 'messages') {
      loadFeed();
      loadAds();
      loadSettings();
    }
  }, [activeTab]);

  const loadSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/config`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setAdFrequency(data.config?.connect_feed?.ad_frequency || 10);
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  };

  const loadAds = async () => {
    try {
      const res = await fetch(`${API_URL}/api/social/feed/ads?limit=10`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setAds(data.ads || []);
      }
    } catch (error) {
      console.error('Failed to load ads:', error);
    }
  };

  const loadFeed = async () => {
    try {
      setLoading(true);
      const endpoint = {
        'for-you': '/api/social/feed/for-you',
        'following': '/api/social/feed/following',
        'everyone': '/api/social/feed/everyone',
        'unified': '/api/connect-runners/feed/unified?feed_type=everyone',
        'runners': null, // Special case - handled by RunnersTab component
        'trending': '/api/social/feed/trending'
      }[activeTab];

      if (!endpoint || activeTab === 'runners') {
        setLoading(false);
        return;
      }

      const res = await fetch(`${API_URL}${endpoint}`, {
        credentials: 'include'
      });
      
      if (res.ok) {
        const data = await res.json();
        setPosts(data.posts || []);
      }
    } catch (error) {
      console.error('Failed to load feed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePost = async () => {
    if (!newPost.trim()) return;
    
    if (newPost.length > 280) {
      toast.error('Post is too long (max 280 characters)');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/social/posts`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: newPost })
      });

      if (res.ok) {
        toast.success('Post created!');
        setNewPost('');
        setShowCreatePost(false);
        loadFeed();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to create post');
      }
    } catch (error) {
      toast.error('Failed to create post');
    }
  };

  const handleSearch = async (query) => {
    setSearchQuery(query);
    
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      setSearchLoading(true);
      const res = await fetch(`${API_URL}/api/social/search?q=${encodeURIComponent(query)}&search_type=all`, {
        credentials: 'include'
      });

      if (res.ok) {
        const data = await res.json();
        setSearchResults(data);  // Now contains {users: [], posts: []}
      }
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleUserAction = (user, action) => {
    if (action === 'profile') {
      navigate(`/connect/profile/${user.username}`);
    } else if (action === 'dm') {
      navigate('/connect/feed?tab=messages&user=' + user.username);
    }
    setSearchQuery('');
    setSearchResults([]);
  };

  const insertAds = (postsList) => {
    if (!ads.length || !adFrequency) return postsList;
    
    const result = [];
    postsList.forEach((post, index) => {
      result.push(post);
      if ((index + 1) % adFrequency === 0 && ads.length > 0) {
        const ad = ads[Math.floor(Math.random() * ads.length)];
        result.push({ ...ad, isAd: true });
      }
    });
    return result;
  };

  const postsWithAds = insertAds(posts);

  const updateDmPrivacy = async (privacy) => {
    try {
      const res = await fetch(`${API_URL}/api/social/user/dm-privacy`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dm_privacy: privacy })
      });

      if (res.ok) {
        setDmPrivacy(privacy);
        toast.success('Privacy setting updated');
      }
    } catch (error) {
      toast.error('Failed to update privacy');
    }
  };

  const getAISuggestion = async () => {
    if (!newPost.trim()) {
      toast.error('Start writing something first');
      return;
    }

    try {
      setShowAIAssistant(true);
      const res = await fetch(`${API_URL}/api/ai/enhance-post`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: newPost })
      });

      if (res.ok) {
        const data = await res.json();
        setAiSuggestion(data.suggestion);
      } else {
        toast.error('AI assistant unavailable');
      }
    } catch (error) {
      toast.error('AI assistant unavailable');
    }
  };

  if (activeTab === 'messages') {
    return <MatriseConnectPage embeddedInConnect={true} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-black">
      <div className="max-w-4xl mx-auto p-4 space-y-4">
        {/* Header */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-2xl font-bold">Matrise Connect</CardTitle>
              <div className="flex gap-2">
                <Button onClick={() => setShowPrivacySettings(true)} variant="outline" size="sm">
                  <Settings className="w-4 h-4" />
                </Button>
                <Button onClick={() => setShowCreatePost(!showCreatePost)} size="sm">
                  {showCreatePost ? <X className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                  {showCreatePost ? 'Cancel' : 'New Post'}
                </Button>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Search */}
        <Card>
          <CardContent className="pt-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            
            {searchResults.users && searchResults.users.length > 0 && searchResults.posts && searchResults.posts.length > 0 && (
              <div className="mt-4 space-y-4">
                {/* Users Section */}
                {searchResults.users.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-500 mb-2">People</h3>
                    <div className="space-y-2">
                      {searchResults.users.map(user => (
                        <UserSearchResult
                          key={user.id}
                          user={user}
                          onAction={handleUserAction}
                        />
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Posts Section */}
                {searchResults.posts && searchResults.posts.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-500 mb-2">Posts</h3>
                    <div className="space-y-2">
                      {searchResults.posts.map(post => (
                        <div
                          key={post.id}
                          className="p-3 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                          onClick={() => {
                            setSearchQuery('');
                            setSearchResults({});
                            // Scroll to post or show post detail
                          }}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold text-sm">{post.display_name}</span>
                            <span className="text-xs text-gray-500">@{post.username}</span>
                          </div>
                          <p className="text-sm line-clamp-2">{post.content}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create Post */}
        {showCreatePost && (
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-4">
                <Textarea
                  placeholder="What's happening in the MTR community?"
                  value={newPost}
                  onChange={(e) => setNewPost(e.target.value)}
                  maxLength={280}
                  rows={3}
                  className="resize-none"
                />
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">
                    {newPost.length}/280
                  </span>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={getAISuggestion}>
                      <Sparkles className="w-4 h-4 mr-1" />
                      AI Enhance
                    </Button>
                    <Button onClick={handleCreatePost} disabled={!newPost.trim()}>
                      <Send className="w-4 h-4 mr-2" />
                      Post
                    </Button>
                  </div>
                </div>

                {showAIAssistant && aiSuggestion && (
                  <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold text-blue-700 dark:text-blue-300">
                        AI Suggestion:
                      </span>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setShowAIAssistant(false)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                    <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">{aiSuggestion}</p>
                    <Button
                      size="sm"
                      onClick={() => {
                        setNewPost(aiSuggestion);
                        setShowAIAssistant(false);
                      }}
                    >
                      Use This
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Feed Tabs */}
        <Card>
          <CardContent className="pt-6">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="for-you">
                  <TrendingUp className="w-4 h-4 mr-2" />
                  For You
                </TabsTrigger>
                <TabsTrigger value="following">
                  <Users className="w-4 h-4 mr-2" />
                  Following
                </TabsTrigger>
                <TabsTrigger value="trending">
                  <TrendingUp className="w-4 h-4 mr-2" />
                  Trending
                </TabsTrigger>
                <TabsTrigger value="messages">
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Messages
                </TabsTrigger>
              </TabsList>

              <TabsContent value={activeTab} className="mt-6 space-y-4">
                {loading ? (
                  <div className="text-center py-8 text-gray-500">Loading...</div>
                ) : postsWithAds.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    {activeTab === 'following' 
                      ? 'Follow users to see their posts here'
                      : 'No posts yet. Be the first to post!'}
                  </div>
                ) : (
                  postsWithAds.map((item, index) => 
                    item.isAd ? (
                      <AdCard key={`ad-${item.id}-${index}`} ad={item} placement="connect_feed" />
                    ) : (
                      <PostCard
                        key={item.id}
                        post={item}
                        onUpdate={loadFeed}
                        onUserAction={handleUserAction}
                        onTipClick={setShowTipDialog}
                      />
                    )
                  )
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>

      {/* Privacy Settings Dialog */}
      <Dialog open={showPrivacySettings} onOpenChange={setShowPrivacySettings}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Message Privacy</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">Choose who can send you direct messages</p>
            <div className="space-y-2">
              <Button
                variant={dmPrivacy === 'public' ? 'default' : 'outline'}
                className="w-full justify-start"
                onClick={() => updateDmPrivacy('public')}
              >
                <Mail className="w-4 h-4 mr-2" />
                Everyone can message me
              </Button>
              <Button
                variant={dmPrivacy === 'mutual_only' ? 'default' : 'outline'}
                className="w-full justify-start"
                onClick={() => updateDmPrivacy('mutual_only')}
              >
                <UserPlusIcon className="w-4 h-4 mr-2" />
                Only mutual followers
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Tip Dialog */}
      {showTipDialog && (
        <Dialog open={true} onOpenChange={() => setShowTipDialog(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Tip @{showTipDialog.username}</DialogTitle>
            </DialogHeader>
            <TipFormComponent
              username={showTipDialog.username}
              postId={showTipDialog.id}
              onSuccess={() => {
                setShowTipDialog(null);
                loadFeed();
              }}
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function UserSearchResult({ user, onAction }) {
  const [showMenu, setShowMenu] = useState(false);

  return (
    <div className="flex items-center gap-3 p-3 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg relative">
      <Avatar onClick={() => onAction(user, 'profile')} className="cursor-pointer">
        <AvatarImage src={user.profile_picture} />
        <AvatarFallback>{user.display_name?.[0]?.toUpperCase()}</AvatarFallback>
      </Avatar>
      <div className="flex-1 cursor-pointer" onClick={() => onAction(user, 'profile')}>
        <div className="flex items-center gap-2">
          <span className="font-semibold">{user.display_name}</span>
          {user.verified && <CheckCircle className="w-4 h-4 text-blue-500" />}
        </div>
        <span className="text-sm text-gray-500">@{user.username}</span>
      </div>
      <div className="relative">
        <Button variant="ghost" size="sm" onClick={() => setShowMenu(!showMenu)}>
          <MoreVertical className="w-4 h-4" />
        </Button>
        {showMenu && (
          <div className="absolute right-0 top-10 bg-white dark:bg-gray-800 shadow-lg rounded-lg border p-2 z-10 min-w-[160px]">
            <button
              onClick={() => { onAction(user, 'profile'); setShowMenu(false); }}
              className="w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded flex items-center gap-2"
            >
              <ExternalLink className="w-4 h-4" />
              View Profile
            </button>
            <button
              onClick={() => { onAction(user, 'dm'); setShowMenu(false); }}
              className="w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded flex items-center gap-2"
            >
              <MessageCircle className="w-4 h-4" />
              Send Message
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function PostCard({ post, onUpdate, onUserAction, onTipClick }) {
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [showMenu, setShowMenu] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState(post.content);

  const loadComments = async () => {
    try {
      const res = await fetch(`${API_URL}/api/social/posts/${post.id}/comments`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setComments(data.comments || []);
      }
    } catch (error) {
      console.error('Failed to load comments:', error);
    }
  };

  useEffect(() => {
    if (showComments) {
      loadComments();
    }
  }, [showComments]);

  const handleLike = async () => {
    try {
      const method = post.liked_by_me ? 'DELETE' : 'POST';
      const res = await fetch(`${API_URL}/api/social/posts/${post.id}/like`, {
        method,
        credentials: 'include'
      });

      if (res.ok) {
        onUpdate();
      }
    } catch (error) {
      console.error('Failed to like post:', error);
    }
  };

  const handleComment = async () => {
    if (!newComment.trim()) return;

    try {
      const res = await fetch(`${API_URL}/api/social/posts/${post.id}/comment`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: newComment })
      });

      if (res.ok) {
        setNewComment('');
        loadComments();
        onUpdate();
        toast.success('Comment added');
      }
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  const handleEdit = async () => {
    try {
      const res = await fetch(`${API_URL}/api/social/posts/${post.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: editContent })
      });

      if (res.ok) {
        setEditing(false);
        onUpdate();
        toast.success('Post updated');
      }
    } catch (error) {
      toast.error('Failed to update post');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this post?')) return;

    try {
      const res = await fetch(`${API_URL}/api/social/posts/${post.id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (res.ok) {
        onUpdate();
        toast.success('Post deleted');
      }
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  return (
    <div className="border-b border-gray-200 dark:border-gray-800 pb-4 last:border-0">
      <div className="flex gap-3">
        <Avatar
          className="cursor-pointer"
          onClick={() => onUserAction({ username: post.username }, 'profile')}
        >
          <AvatarImage src={post.profile_picture} />
          <AvatarFallback>{post.display_name?.[0]?.toUpperCase()}</AvatarFallback>
        </Avatar>
        
        <div className="flex-1 space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span
                className="font-semibold hover:underline cursor-pointer"
                onClick={() => onUserAction({ username: post.username }, 'profile')}
              >
                {post.display_name}
              </span>
              {post.verified && <CheckCircle className="w-4 h-4 text-blue-500" />}
              <span className="text-sm text-gray-500">
                @{post.username} · {formatTimeAgo(post.created_at)}
              </span>
              {post.edited && <span className="text-xs text-gray-400">(edited)</span>}
            </div>
            {post.is_own && (
              <div className="relative">
                <Button variant="ghost" size="sm" onClick={() => setShowMenu(!showMenu)}>
                  <MoreVertical className="w-4 h-4" />
                </Button>
                {showMenu && (
                  <div className="absolute right-0 top-8 bg-white dark:bg-gray-800 shadow-lg rounded-lg border p-2 z-10 min-w-[120px]">
                    <button
                      onClick={() => { setEditing(true); setShowMenu(false); }}
                      className="w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded flex items-center gap-2"
                    >
                      <Edit2 className="w-4 h-4" />
                      Edit
                    </button>
                    <button
                      onClick={() => { handleDelete(); setShowMenu(false); }}
                      className="w-full text-left px-3 py-2 hover:bg-red-100 dark:hover:bg-red-900 rounded flex items-center gap-2 text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {editing ? (
            <div className="space-y-2">
              <Textarea
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                maxLength={280}
                rows={3}
              />
              <div className="flex gap-2">
                <Button size="sm" onClick={handleEdit}>Save</Button>
                <Button size="sm" variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
              </div>
            </div>
          ) : (
            <p className="text-base whitespace-pre-wrap">{post.content}</p>
          )}

          <div className="flex items-center gap-6 text-gray-500">
            <button
              className="flex items-center gap-1 hover:text-blue-500 transition-colors"
              onClick={() => setShowComments(!showComments)}
            >
              <MessageCircle className="w-4 h-4" />
              <span className="text-sm">{post.comments_count || 0}</span>
            </button>

            <button
              className={`flex items-center gap-1 transition-colors ${
                post.liked_by_me ? 'text-red-500' : 'hover:text-red-500'
              }`}
              onClick={handleLike}
            >
              <Heart className={`w-4 h-4 ${post.liked_by_me ? 'fill-current' : ''}`} />
              <span className="text-sm">{post.likes_count || 0}</span>
            </button>

            <button className="flex items-center gap-1 hover:text-green-500 transition-colors">
              <Repeat2 className="w-4 h-4" />
              <span className="text-sm">{post.reposts_count || 0}</span>
            </button>
            
            <button
              className="flex items-center gap-1 hover:text-yellow-500 transition-colors"
              onClick={() => onTipClick(post)}
              title="Tip user in MTR"
            >
              <DollarSign className="w-4 h-4" />
              <span className="text-sm">Tip</span>
            </button>
          </div>

          {/* Tip Statistics */}
          <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Total {(post.total_tipped || 0).toFixed(2)} MTR tipped by {post.supporters_count || 0} {post.supporters_count === 1 ? 'person' : 'people'}
          </div>

          {showComments && (
            <div className="mt-4 space-y-3 border-t pt-3">
              <div className="flex gap-2">
                <Input
                  placeholder="Write a comment..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  maxLength={280}
                  onKeyDown={(e) => e.key === 'Enter' && handleComment()}
                />
                <Button size="sm" onClick={handleComment} disabled={!newComment.trim()}>
                  <Send className="w-4 h-4" />
                </Button>
              </div>
              
              {comments.map(comment => (
                <CommentCard key={comment.id} comment={comment} onUpdate={loadComments} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CommentCard({ comment, onUpdate }) {
  const [liked, setLiked] = useState(comment.liked_by_me || false);
  const [likesCount, setLikesCount] = useState(comment.likes_count || 0);

  const handleLike = async () => {
    try {
      const method = liked ? 'DELETE' : 'POST';
      const res = await fetch(`${API_URL}/api/social/comments/${comment.id}/like`, {
        method,
        credentials: 'include'
      });

      if (res.ok) {
        setLiked(!liked);
        setLikesCount(liked ? likesCount - 1 : likesCount + 1);
      }
    } catch (error) {
      console.error('Failed to like comment:', error);
    }
  };

  return (
    <div className="flex gap-2 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
      <Avatar className="w-8 h-8">
        <AvatarImage src={comment.profile_picture} />
        <AvatarFallback>{comment.display_name?.[0]?.toUpperCase()}</AvatarFallback>
      </Avatar>
      <div className="flex-1 space-y-1">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-sm">{comment.display_name}</span>
          <span className="text-xs text-gray-500">@{comment.username}</span>
          <span className="text-xs text-gray-400">·</span>
          <span className="text-xs text-gray-500">{formatTimeAgo(comment.created_at)}</span>
        </div>
        <p className="text-sm">{comment.content}</p>
        <button
          className={`flex items-center gap-1 text-xs transition-colors ${
            liked ? 'text-red-500' : 'text-gray-500 hover:text-red-500'
          }`}
          onClick={handleLike}
        >
          <Heart className={`w-3 h-3 ${liked ? 'fill-current' : ''}`} />
          <span>{likesCount}</span>
        </button>
      </div>
    </div>
  );
}

function TipFormComponent({ username, postId, onSuccess }) {
  const [amount, setAmount] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!amount || parseFloat(amount) < 1) {
      toast.error('Minimum tip is 1 MTR');
      return;
    }

    try {
      setSending(true);
      const res = await fetch(`${API_URL}/api/token-actions/tip`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username,
          amount: parseFloat(amount),
          message,
          post_id: postId
        })
      });

      if (res.ok) {
        onSuccess();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to send tip');
      }
    } catch (error) {
      toast.error('Failed to send tip');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium mb-2">Amount (MTR)</label>
        <Input
          type="number"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="10"
          min="1"
        />
      </div>
      <div>
        <label className="block text-sm font-medium mb-2">Message (optional)</label>
        <Input
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Great post!"
        />
      </div>
      <Button onClick={handleSend} disabled={sending} className="w-full">
        {sending ? 'Sending...' : `Send ${amount || '0'} MTR`}
      </Button>
    </div>
  );
}
